import {
  Binoculars,
  Bird,
  BookOpen,
  Bug,
  Camera,
  CircleDot,
  Compass,
  Fish,
  Flower2,
  Leaf,
  Map,
  Mountain,
  PawPrint,
  Rabbit,
  Sprout,
  Trees,
  Waves
} from 'lucide-react';
import type { ChapterNavItem, EcologyNode, GuideEntry, ReadingSection, ResourceLink } from './types';

export const chapterNav: ChapterNavItem[] = [
  { id: 'day1', day: 'Day 1', title: '出发，向草原', subtitle: '海拉尔、博物馆与古城', icon: Compass },
  { id: 'day2', day: 'Day 2', title: '弯弯的河', subtitle: '草原的丝带', icon: Waves },
  { id: 'day3', day: 'Day 3', title: '白桦与马背', subtitle: '森林边缘的生活', icon: Trees },
  { id: 'day4', day: 'Day 4', title: '寻找驯鹿', subtitle: '走进大兴安岭', icon: Leaf },
  { id: 'day5', day: 'Day 5', title: '边境与蒙古包', subtitle: '河流、坡地和家', icon: Mountain },
  { id: 'day6', day: 'Day 6', title: '满洲里', subtitle: '边境童话城', icon: Camera },
  { id: 'day7', day: 'Day 7', title: '湖边告别', subtitle: '呼和诺尔湖', icon: Fish },
  { id: 'guide', day: 'Field Guide', title: '草原图鉴', subtitle: '动物、昆虫、鸟类', icon: Binoculars }
];

export const dayTwoSections: ReadingSection[] = [
  {
    id: 'river',
    eyebrow: '莫日格勒河',
    title: '一条会移动的蓝色线',
    deck: '从山顶看，莫日格勒河像丝带；从地理上看，它是一条正在慢慢改写地图的河。',
    image: '/assets/day2_morigele_meander.jpg',
    imageAlt: '莫日格勒河河曲俯瞰图',
    imageCredit: '本书素材：莫日格勒河河曲俯瞰',
    body: [
      '莫日格勒河的美，不只是“弯弯曲曲”。它流过比较平坦的草原，水没有一直往下冲的急坡，就会在细小的偏转里慢慢摆动，越走越弯。',
      '河弯外侧的水流更急，会一点点冲刷岸边；内侧的水慢下来，泥沙更容易落下。这样一边被削走，一边被堆高，河弯就像自己会长大一样。',
      '当一个弯道长到快碰到自己时，洪水可能冲开更短的新路。旧弯道会被留下，变成湿地、浅水洼，甚至是牛轭湖。你在高处看到的旧水痕，就是河流写在草原上的旧句子。'
    ],
    facts: ['关键词：蜿蜒型河流', '外侧冲刷，内侧堆积', '旧河道可能变成湿地或牛轭湖'],
    lookFor: ['找最弯的河段，看它像不像一个快合上的圆环。', '比较河弯两侧：哪边更陡，哪边像浅浅的滩？', '找断续的水面或深绿草带，它可能是旧河道留下的痕迹。'],
    whyItMatters: '读懂河弯，就能看出水不是静止的风景。它在搬运泥沙、改变岸边，也在给草地和湿地送水。',
    fieldQuestion: '如果几年以后再来，哪一段河岸最可能变样？',
    resourceIds: ['cng-river', 'usgs-meander', 'ngs-oxbow', 'britannica-meander'],
    callout: {
      title: '家长可以补充',
      text: '中国国家地理把莫尔格勒河作为蜿蜒型河流典型来讲，重点不是背术语，而是让孩子看懂“凹岸冲刷、凸岸堆积、裁弯取直”这条因果链。'
    }
  },
  {
    id: 'grassland',
    eyebrow: '呼伦贝尔草原',
    title: '草原不是一张绿色地毯',
    deck: '同一片草原里，会出现深绿、浅绿、偏黄和湿润的斑块。',
    image: '/assets/day2_grassland_river_overlook.jpg',
    imageAlt: '草原与河流俯瞰图',
    imageCredit: '本书素材：草原和河流俯瞰',
    body: [
      '车窗外的草原看起来很大、很绿，但它不是同一种草铺成的地毯。草原里有草本植物、野花、种子、昆虫、鸟、土壤微生物，也有牛、羊、马和人类放牧生活。',
      '河边更绿，常常是因为水分更足；坡上偏浅，可能是土层更薄、风更大；湿地旁草更高，是因为那里有更稳定的水和泥。颜色变化，就是生态线索。',
      '草原的“运转”也不是只靠草。植物吸收阳光生长，牛羊马和昆虫取食植物，鸟吃昆虫，微生物分解枯草和粪便，养分又回到土壤。'
    ],
    facts: ['生产者：草和野花', '消费者：昆虫、鸟、牛羊马', '分解者：真菌、细菌等微生物'],
    lookFor: ['找三种不同的绿色：河边、坡上、营地旁边。', '观察牛羊马是不是集中在水草更好的地方。', '找草丛里的小花、虫鸣或跳起的小昆虫。'],
    whyItMatters: '草原不是一块绿色背景，而是一个能量流动的系统。草把阳光变成身体，动物和微生物把这些能量继续传下去。',
    fieldQuestion: '如果一块草地被车轮反复碾过，哪些生命会先受影响？',
    resourceIds: ['ibcas-grassland', 'species-database', 'csf-insects'],
    callout: {
      title: '家长可以补充',
      text: '孩子先看颜色和动物位置；家长再把它连到“能量流动”和“物质循环”。草原不是背景，而是一个正在工作的生态系统。'
    }
  },
  {
    id: 'camp',
    eyebrow: '呼籁营地',
    title: '营地也有地理答案',
    deck: '营地为什么在平坦开阔处？为什么不直接建在湿地里？',
    image: '/assets/day2_hulai_camp.jpg',
    imageAlt: '呼籁营地写实场景图',
    imageCredit: '本书素材：呼籁营地',
    body: [
      '营地不是随便选出来的。平坦、开阔、靠近道路，说明这里适合集合、休息和活动，也方便车辆和人群安全移动。',
      '如果地面太湿，车容易陷进去，草根也容易被压坏；如果坡太陡，人走路、停车、搭帐篷都不安全；如果离道路太远，草地会被车轮反复碾出新痕迹。',
      '一个好观察者会把“我们到了一个营地”，继续读成“这里为什么适合停下来”。旅行不是离自然越近越好，而是用尽量少的打扰去看见自然。'
    ],
    facts: ['平坦：方便停留和集合', '避开湿地：保护脆弱地面', '靠近道路：减少车轮压草'],
    lookFor: ['营地地面是不是比旁边更平坦、更干？', '车道有没有沿着已有道路走，而不是横穿草地？', '营地和湿地之间有没有留出距离？'],
    whyItMatters: '好的旅行点位会把人的活动集中在更耐受的地方。这样既能安全停留，也能减少对湿地、草根和动物活动区的干扰。',
    fieldQuestion: '如果你要给一队人选休息点，第一眼会先排除哪里？',
    resourceIds: ['ibcas-grassland', 'wetland-law'],
    callout: {
      title: '一起想一想',
      text: '如果你是领队，要给几十个人选一个草原休息点，你会先避开什么地方？湿地、陡坡、离路太远的位置，还是牛羊正在集中吃草的地方？'
    }
  },
  {
    id: 'wetland',
    eyebrow: '湿地远眺',
    title: '湿地像草原里的海绵和餐厅',
    deck: '湿地不是普通水坑，而是存水、净水、喂养生命的地方。',
    image: '/assets/day2_wetland.jpg',
    imageAlt: '草原湿地写实场景图',
    imageCredit: '本书素材：草原湿地',
    body: [
      '湿地是长期或季节性有水的地方。它可能是浅水、泥滩、湿草甸、芦苇丛、小水塘，也可能是一条河旁边低低的草地。',
      '湿地像海绵，能暂时存住一部分水；也像滤网，让水慢慢经过植物、泥土和微生物。它还能给昆虫、水鸟、两栖动物和水生植物提供食物与藏身处。',
      '观察湿地最好站在边缘，而不是踩进去。你可以看反光水面、深绿色草带、鸟的活动、蜻蜓飞行和低洼地形。看见这些，就已经读到湿地的秘密。'
    ],
    facts: ['涵养水源', '净化水质', '调蓄洪水', '维护生物多样性'],
    lookFor: ['找水面反光、泥滩、芦苇或深绿色湿草甸。', '看鸟是在水面、浅水边，还是草丛边活动。', '观察蜻蜓是不是沿水边来回飞。'],
    whyItMatters: '湿地能存水、慢慢释放水，也能让很多小生命有地方吃饭、躲藏和繁殖。它不是“空地上的水坑”，而是草原的关键器官。',
    fieldQuestion: '为什么保护湿地常常要求人在边缘观察，而不是走进去？',
    resourceIds: ['nfga-wetland', 'cas-wetland', 'hulun-birds'],
    callout: {
      title: '家长可以补充',
      text: '国家林草局把湿地的功能概括为涵养水源、净化水质、调蓄洪水、调节气候和维护生物多样性。儿童版先用“海绵”理解储水，再扩展到净化和栖息地。'
    }
  },
  {
    id: 'obao',
    eyebrow: '敖包',
    title: '方向、记忆和敬意',
    deck: '草原辽阔，人会用地标把空间变得可以识别。',
    image: '/assets/day2_obao_hill.jpg',
    imageAlt: '山顶敖包写实场景图',
    imageCredit: '本书素材：山顶敖包',
    body: [
      '敖包不是普通石堆。它也叫“脑包”“鄂博”，有“堆子”的意思，很多敖包由石块堆成，常建在山顶或丘陵上。',
      '在没有密集路牌的辽阔草原上，高处的敖包容易被看见，也容易成为方向、记忆和礼俗的中心。人们来到这里，不只是看一个物体，也是在和一片地方建立关系。',
      '在敖包旁边，不攀爬，不拿走石头，不拉扯彩旗或哈达。理解一个地方，也包括尊重它。'
    ],
    facts: ['常建在山顶或丘陵', '是草原地标', '与祭敖包民俗相关'],
    lookFor: ['它是不是在高处，远远就能看见？', '周围有没有彩旗、哈达或石块堆叠？', '人们靠近时动作是不是更安静、更郑重？'],
    whyItMatters: '敖包把自然地形、方向识别和礼俗记忆连在一起。它提醒我们：草原不是只有自然，也有人怎样理解和尊重自然。',
    fieldQuestion: '如果没有手机地图，草原上的人会怎样记住方向和地点？',
    resourceIds: ['obao-ich'],
    callout: {
      title: '一起想一想',
      text: '城市里有门牌、地铁站和路口。草原上有哪些东西可以成为“地址”？山丘、河湾、敖包、营地和道路，都可能帮助人们辨认位置。'
    }
  },
  {
    id: 'viewpoint',
    eyebrow: '牧云山顶',
    title: '站高一点，地图就出现了',
    deck: '河边只能看见水，山顶能看见河为什么这样走。',
    image: '/assets/day2_family_viewpoint.jpg',
    imageAlt: '一家人在高处俯瞰草原河流',
    imageCredit: '本书素材：牧云山顶远眺',
    body: [
      '站在河边，你会先看见水声、草、泥和风；站到山顶，你会看见河弯、旧河道、湿地、道路、营地和动物位置连成一张图。',
      '这就是“尺度”的变化。近看能发现细节，远看能发现关系。一个好的自然观察者，要会在两种尺度之间切换。',
      '今天在高处可以试着找三条线：银色的水线，深绿的湿地线，还有人走出来的道路线。三条线交在一起，就是草原上水、生命和人的关系。'
    ],
    facts: ['近看细节', '远看关系', '用路线、水线、草色线读地图'],
    lookFor: ['从高处找银色水线、深绿湿地线和浅色道路。', '看动物是不是沿水线或草色变化分布。', '把刚刚走过的路线在眼前风景里找出来。'],
    whyItMatters: '高处视角能把零散景物连成一张地图。孩子会发现：河、草、路、营地和动物不是分开的，它们互相影响。',
    fieldQuestion: '如果你要画一张今天的地图，第一笔会画河、路，还是营地？',
    resourceIds: ['cctv-photo', 'xinhua-morigele'],
    callout: {
      title: '家长可以补充',
      text: '孩子看到“漂亮风景”以后，可以继续问：为什么这里适合观景？为什么河边更绿？为什么道路不直接穿过湿地？这些问题会把审美变成地理思考。'
    }
  },
  {
    id: 'life',
    eyebrow: '草原生命',
    title: '从草叶到天空的小生命',
    deck: '草原不只有牛羊马。昆虫、鸟类和微生物，让这里真正活起来。',
    image: '/assets/day2_barn_swallow.jpg',
    imageAlt: '家燕专业照片',
    imageCredit: 'Michael Gäbler / Wikimedia Commons / CC BY-SA 3.0',
    body: [
      '燕子在草地和水面上空来回飞，不是在玩杂技，而是在空中捕食小昆虫。水边昆虫多，所以你更容易在河边和湿地附近看见它们低飞转弯。',
      '蚂蚱吃草，也会被鸟吃；甲虫会处理粪便和腐烂植物；蜻蜓的幼虫生活在水里，成虫在空中捕食。小生命把草地、水边和天空连起来。',
      '旅途中不需要抓住它们。你只要记下：它在哪里？在飞、跳、停，还是取食？旁边有什么植物和水？这些信息比“我看见一只虫”更有价值。'
    ],
    facts: ['燕子：空中捕虫', '蚂蚱：草叶取食者', '甲虫：清理和分解', '蜻蜓：连接水和天空'],
    lookFor: ['燕子是不是在水面或草地上空低飞转弯？', '草丛里有没有跳起的蚂蚱，或停在叶片上的小昆虫？', '水边有没有蜻蜓停在草尖，再突然飞起？'],
    whyItMatters: '小动物常常是生态系统的信号。昆虫多，鸟就可能多；湿地健康，水边飞行者也更容易出现。',
    fieldQuestion: '如果今天看不见昆虫，可能是天气、时间，还是环境不同？',
    resourceIds: ['kepuchina-swallow', 'csf-insects', 'species-database'],
    callout: {
      title: '家长可以补充',
      text: '孩子如果拍到清晰照片，后续可以用专业物种数据库或本地自然教育资料核对。书里先讲“生态角色”，避免把旅途中可能看见的动物过早写死成某一个物种。'
    }
  }
];

export const guideEntries: GuideEntry[] = [
  {
    id: 'horse',
    category: 'animals',
    name: '蒙古马',
    type: '草原伙伴',
    signal: '开阔地移动，耐力好',
    childText: '马不是只用来骑的动物。草原生活里，马曾经帮助人移动、放牧、运输，也成为很多故事和礼仪的一部分。',
    deeperText: '蒙古马体型不一定高大，但耐寒、耐粗饲、耐力强。给孩子讲马时，不要只讲“骑马项目”，要讲它和草原生活方式的关系。',
    question: '为什么开阔草原比密林更适合马奔跑？',
    icon: PawPrint,
    image: '/assets/day2_mongolian_horse.jpg',
    imageAlt: '内蒙古草原上的马',
    imageCredit: 'Michael Mooney / Wikimedia Commons / CC BY 2.0',
    imageSource: 'https://commons.wikimedia.org/wiki/File:Guide_%26_horse_Inner_Mongolia.jpg'
  },
  {
    id: 'cattle',
    category: 'animals',
    name: '牛',
    type: '吃草方式',
    signal: '需要水，也吃较高较粗的草',
    childText: '牛常用舌头卷住草再咬断。它们体型大，需要的食物和水也多，所以水草好的地方常能看见牛。',
    deeperText: '不同草食动物的口器和取食高度不同，会影响植物被取食后的高度和再生节奏。这是理解放牧压力的入口。',
    question: '牛为什么常常离水源不太远？',
    icon: PawPrint,
    image: '/assets/day2_grazing_animals.jpg',
    imageAlt: '草原上放牧的牛羊马',
    imageCredit: '本书素材：草原放牧动物'
  },
  {
    id: 'ground-squirrel',
    category: 'animals',
    name: '草原鼠类',
    type: '地下工程师',
    signal: '洞口、土堆、警戒叫声',
    childText: '草原上一些鼠类会挖洞。洞穴能帮助它们躲避天敌，也会改变土壤和植物生长的小环境。',
    deeperText: '正式定稿时应按实地观察核验具体物种。儿童阅读中可先讲“洞穴动物如何改变草原”，避免把所有鼠类都写成同一种。',
    question: '一个小洞口，可能怎样影响旁边的草和土？',
    icon: Rabbit,
    image: '/assets/day2_ground_squirrel.jpg',
    imageAlt: '草原地松鼠类动物示意照片',
    imageCredit: 'Bouke ten Cate / Wikimedia Commons / CC BY-SA 4.0',
    imageSource: 'https://commons.wikimedia.org/wiki/File:European_ground_squirrel_-_Siesel_-_Spermophilus_citellus_02.jpg'
  },
  {
    id: 'grasshopper',
    category: 'insects',
    name: '蚂蚱',
    type: '草叶取食者',
    signal: '草丛里跳起的小身体',
    childText: '蚂蚱吃植物，也会被鸟、蜘蛛和其他动物吃。它们让你看见：草原食物网不是一条线。',
    deeperText: '草原昆虫既可能是植食者，也可能是传粉者、分解者或其他动物的食物。数量变化还会受到天气、植被和天敌影响。',
    question: '如果草丛里没有昆虫，鸟会少掉哪些食物？',
    icon: Bug,
    image: '/assets/day2_grasshopper.jpg',
    imageAlt: '草地蚂蚱特写照片',
    imageCredit: 'Rouibi Dhia Eddine Nadjm / Wikimedia Commons / CC BY-SA 4.0',
    imageSource: 'https://commons.wikimedia.org/wiki/File:Brown_grasshopper_macro.jpg'
  },
  {
    id: 'beetle',
    category: 'insects',
    name: '甲虫',
    type: '分解与捕食',
    signal: '地面、粪便旁、草根附近',
    childText: '有些甲虫会处理粪便和腐烂植物，有些会捕食小虫。它们虽然小，却参与清理和循环。',
    deeperText: '粪食性昆虫能帮助分解动物粪便，让养分回到土壤。孩子可以从“草原谁在打扫卫生”进入分解者概念。',
    question: '如果没有分解者，草原上的枯草和粪便会怎样？',
    icon: CircleDot,
    image: '/assets/day2_dung_beetle.jpg',
    imageAlt: '蜣螂推动粪球的照片',
    imageCredit: 'Icem4k / Wikimedia Commons / CC BY-SA 4.0',
    imageSource: 'https://commons.wikimedia.org/wiki/File:Dung_Beetle_making_a_dung_ball_04.jpg'
  },
  {
    id: 'dragonfly',
    category: 'insects',
    name: '蜻蜓',
    type: '湿地飞行者',
    signal: '水边停飞，快速转向',
    childText: '蜻蜓常在水边出现。它们的幼虫生活在水里，成虫会在空中捕食小昆虫。',
    deeperText: '蜻蜓把水域和陆地连接起来：幼虫阶段依赖水，成虫阶段进入空中食物网。很适合解释“水陆交界”。',
    question: '为什么湿地边比干草地更容易看见蜻蜓？',
    icon: Bug,
    image: '/assets/day2_dragonfly.jpg',
    imageAlt: '蜻蜓专业特写照片',
    imageCredit: 'Charles J. Sharp / Wikimedia Commons / CC BY-SA 4.0',
    imageSource: 'https://commons.wikimedia.org/wiki/File:Hairy_dragonfly_(Brachytron_pratense)_male_close_up.jpg'
  },
  {
    id: 'swallow',
    category: 'birds',
    name: '燕子',
    type: '空中捕虫者',
    signal: '低空飞行，快速转弯',
    childText: '燕子常在水边或草地上空飞来飞去。它们不是乱飞，而是在空中捕食小昆虫。',
    deeperText: '湿地和水边昆虫多，常吸引以昆虫为食的鸟类。孩子看见飞鸟，可以先判断它是在觅食、迁飞还是停歇。',
    question: '燕子为什么喜欢在水面和草地上空来回飞？',
    icon: Bird,
    image: '/assets/day2_barn_swallow.jpg',
    imageAlt: '家燕专业照片',
    imageCredit: 'Michael Gäbler / Wikimedia Commons / CC BY-SA 3.0',
    imageSource: 'https://commons.wikimedia.org/wiki/File:Hirundo_rustica_(Linnaeus,_1758).jpg'
  },
  {
    id: 'waterbird',
    category: 'birds',
    name: '水鸟',
    type: '湿地访客',
    signal: '水面游动或浅水边走动',
    childText: '水鸟需要水、食物和安全停歇处。浅水、泥滩和水草边，常常是它们寻找食物的地方。',
    deeperText: '呼伦贝尔的湖泊和湿地处在重要候鸟迁飞通道上。正式定稿时可按实地照片再识别到具体种类。',
    question: '水鸟为什么不只需要水，还需要水边的草和泥滩？',
    icon: Bird,
    image: '/assets/day2_whooper_swan.jpg',
    imageAlt: '大天鹅水鸟照片',
    imageCredit: 'Estormiz / Wikimedia Commons / CC0',
    imageSource: 'https://commons.wikimedia.org/wiki/File:Cygnus_cygnus_Oulu_20170417_02.jpg'
  },
  {
    id: 'raptor',
    category: 'birds',
    name: '猛禽',
    type: '高处观察者',
    signal: '空中盘旋，视野开阔处出现',
    childText: '有些猛禽会在空中盘旋寻找猎物。开阔草原让它们更容易看见地面上的小动物。',
    deeperText: '不要承诺旅途中一定能看见猛禽。它适合放在“可能遇到”的图鉴里，用来讲食物链高层和开阔地视野。',
    question: '为什么开阔地方适合从高空寻找猎物？',
    icon: Bird,
    image: '/assets/day2_steppe_eagle.jpg',
    imageAlt: '草原雕飞行示意照片',
    imageCredit: 'PJeganathan / Wikimedia Commons / CC BY-SA 4.0',
    imageSource: 'https://commons.wikimedia.org/wiki/File:Steppe_eagle_in_flight_JEG4594.jpg'
  },
  {
    id: 'meander',
    category: 'geography',
    name: '河曲',
    type: '会移动的线',
    signal: '连续 S 形弯道',
    childText: '河曲就是河流弯曲的部分。水流冲刷外侧，泥沙堆在内侧，河弯会慢慢变大。',
    deeperText: '莫尔格勒河被中国国家地理称为蜿蜒型河流的典型。它适合用来讲冲积平原、凹岸、凸岸和裁弯取直。',
    question: '为什么站在山顶比站在河边更容易看懂河曲？',
    icon: Waves,
    image: '/assets/day2_morigele_meander.jpg',
    imageAlt: '莫日格勒河河曲俯瞰',
    imageCredit: '本书素材：莫日格勒河河曲俯瞰'
  },
  {
    id: 'wetland',
    category: 'geography',
    name: '湿地',
    type: '草原海绵',
    signal: '浅水、泥滩、深绿植物',
    childText: '湿地能存水，也给鸟、昆虫和水生植物提供生活空间。它很有用，也很容易被踩坏。',
    deeperText: '湿地具有涵养水源、净化水质、调蓄洪水、调节气候和维护生物多样性的功能。儿童版先从“海绵”讲起。',
    question: '为什么观察湿地最好站在边上，而不是踩进去？',
    icon: Sprout,
    image: '/assets/day2_wetland.jpg',
    imageAlt: '草原湿地实景',
    imageCredit: '本书素材：草原湿地'
  },
  {
    id: 'food-web',
    category: 'geography',
    name: '食物网',
    type: '关系网络',
    signal: '草、昆虫、鸟、动物、土壤连在一起',
    childText: '草被动物吃，昆虫也吃草，鸟吃昆虫，粪便和枯草被分解者处理。许多关系连起来，就是食物网。',
    deeperText: '食物网比食物链更接近真实生态。Day 2 可以把河边水草、牛羊马、昆虫、鸟和微生物连成一张图。',
    question: '如果昆虫少了，哪些动物可能跟着受影响？',
    icon: Flower2,
    image: '/assets/day2_food_web.svg',
    imageAlt: '草原食物网图解',
    imageCredit: '本书制作：草原食物网图解'
  }
];

export const ecologyNodes: EcologyNode[] = [
  { id: 'grass', name: '草', role: '生产者', detail: '用阳光、水和二氧化碳制造身体，也给动物提供食物。', x: 49, y: 18 },
  { id: 'insects', name: '昆虫', role: '取食者 / 食物', detail: '吃植物、传粉、分解，也会成为鸟和蜘蛛的食物。', x: 22, y: 45 },
  { id: 'birds', name: '鸟', role: '捕食者 / 迁飞者', detail: '吃昆虫、鱼虾或植物种子，也能提示湿地是否适合生命停留。', x: 77, y: 45 },
  { id: 'grazers', name: '牛羊马', role: '大型草食动物', detail: '取食草本植物，也把粪便带回土壤循环。', x: 35, y: 74 },
  { id: 'soil', name: '土壤微生物', role: '分解者', detail: '分解枯草和粪便，让养分重新回到土壤。', x: 65, y: 76 }
];

export const routeStops = ['额尔古纳附近出发', '呼籁营地', '敖包', '湿地远眺', '牧云山顶', '莫日格勒河'];

export const resourceLinks: ResourceLink[] = [
  {
    id: 'cng-river',
    topic: 'river',
    kind: 'article',
    title: '景观：河流',
    source: '中国国家地理',
    url: 'https://www.dili360.com/index.php/article/p5350c3da6f1ec50.htm',
    summary: '用莫尔格勒河解释蜿蜒型河流、冲积平原、河曲和裁弯取直。',
    audience: '家长深入'
  },
  {
    id: 'usgs-meander',
    topic: 'river',
    kind: 'article',
    title: 'Find-A-Feature: Meander',
    source: 'U.S. Geological Survey',
    url: 'https://www.usgs.gov/educational-resources/find-feature-meander',
    summary: '简洁解释 meander 就是河流弯道，适合孩子看图找河曲。',
    audience: '孩子可读'
  },
  {
    id: 'ngs-oxbow',
    topic: 'river',
    kind: 'article',
    title: 'Oxbow Lake',
    source: 'National Geographic Education',
    url: 'https://education.nationalgeographic.org/resource/oxbow-lake/',
    summary: '用图文解释河曲怎样被切断，形成牛轭湖。',
    audience: '亲子共读'
  },
  {
    id: 'britannica-meander',
    topic: 'river',
    kind: 'video',
    title: 'Formation of meanders explained',
    source: 'Encyclopaedia Britannica',
    url: 'https://www.britannica.com/video/meanders-formation-rivers-streams-disturbances-disturbance-stream/-203814',
    summary: '视频解释水流扰动、流速变化和河曲形成，适合家长先看再讲。',
    audience: '家长讲解'
  },
  {
    id: 'cctv-morigele',
    topic: 'video',
    kind: 'video',
    title: '穿越呼伦贝尔大草原，领略“天下第一曲水”',
    source: 'CCTV 纪录',
    url: 'https://www.youtube.com/watch?v=nzxlMQ2NaHc',
    summary: '视频形式看莫日格勒河与呼伦贝尔草原景观，适合旅行前后一起看。',
    audience: '亲子观看'
  },
  {
    id: 'cctv-photo',
    topic: 'river',
    kind: 'gallery',
    title: '夏日莫尔格勒河',
    source: '央视网图片频道',
    url: 'https://photo.cctv.com/2023/07/02/PHOAO8twNV1jITlufXumUjRU230702.shtml',
    summary: '无人机照片展示莫尔格勒河在草原上蜿蜒曲折的真实景观。',
    audience: '孩子看图'
  },
  {
    id: 'xinhua-morigele',
    topic: 'river',
    kind: 'gallery',
    title: '美丽中国：夏日莫尔格勒河',
    source: '新华网',
    url: 'https://www.news.cn/photo/2023-07/01/c_1129727542.htm',
    summary: '新华社图集可用来观察河曲、水草和高处视角。',
    audience: '亲子看图'
  },
  {
    id: 'hulunbuir-museum',
    topic: 'culture',
    kind: 'article',
    title: '呼伦贝尔博物院',
    source: '呼伦贝尔博物院',
    url: 'https://www.hlbebwy.com/',
    summary: '了解呼伦贝尔博物院系统、展览、预约导览和各馆信息。',
    audience: '家长查阅'
  },
  {
    id: 'hulunbuir-history-museum',
    topic: 'culture',
    kind: 'article',
    title: '呼伦贝尔历史博物馆',
    source: '呼伦贝尔历史博物馆',
    url: 'https://hlbelsbwg.hlbebwy.com/introduction',
    summary: '了解历史博物馆位置、展厅结构和地方历史文化展陈。',
    audience: '亲子共读'
  },
  {
    id: 'hulunbuir-name-origin',
    topic: 'culture',
    kind: 'article',
    title: '呼伦贝尔名称的由来',
    source: '内蒙古党史方志网',
    url: 'https://www.nmgqq.com.cn/quqinggailan/dimingsuyuan/hulunbeier/diminggushi/2025-03-04/430396.html',
    summary: '从清代移民实边、正阳街一带建城和呼伦贝尔城命名讲起。',
    audience: '家长讲解'
  },
  {
    id: 'ibcas-grassland',
    topic: 'grassland',
    kind: 'article',
    title: '为恢复退化的草原开“药方”',
    source: '中国科学院植物研究所',
    url: 'https://www.ibcas.ac.cn/2019gb/kepu/yuandi/201712/t20171205_4906643.html',
    summary: '从生产者、消费者、分解者讲草原生态系统的能量和物质循环。',
    audience: '家长讲解'
  },
  {
    id: 'nfga-wetland',
    topic: 'wetland',
    kind: 'article',
    title: '湿地对水生态环境保护有什么作用？',
    source: '国家林业和草原局',
    url: 'https://www.forestry.gov.cn/c/www/zhzs/501631.jhtml',
    summary: '解释湿地的涵养水源、净化水质、调蓄洪水和生物多样性功能。',
    audience: '家长深入'
  },
  {
    id: 'cas-wetland',
    topic: 'wetland',
    kind: 'article',
    title: '湿地是地球之“肾”',
    source: '中国科学院东北地理与农业生态研究所',
    url: 'https://neigae.cas.cn/kxcb/kpwz/201312/t20131211_3995850.html',
    summary: '用“地球之肾”解释湿地净化、调节水量和动物栖息地功能。',
    audience: '家长讲解'
  },
  {
    id: 'wetland-law',
    topic: 'wetland',
    kind: 'policy',
    title: '中华人民共和国湿地保护法',
    source: '生态环境部',
    url: 'https://www.mee.gov.cn/ywgz/fgbz/fl/202112/t20211227_965347.shtml',
    summary: '了解为什么湿地不是普通水坑，而是需要保护和管理的生态空间。',
    audience: '家长查阅'
  },
  {
    id: 'hulun-birds',
    topic: 'birds',
    kind: 'article',
    title: '内蒙古呼伦湖多措并举护航秋季候鸟迁徙',
    source: '国家林业和草原局',
    url: 'https://www.forestry.gov.cn/c/www/dfdt/645606.jhtml',
    summary: '说明呼伦湖位于东亚-澳大利西亚候鸟迁飞路线，是水鸟重要栖息地。',
    audience: '家长深入'
  },
  {
    id: 'migration-plan',
    topic: 'birds',
    kind: 'policy',
    title: '候鸟迁飞通道保护修复中国行动计划（2024-2030 年）',
    source: '国家发展改革委',
    url: 'https://www.ndrc.gov.cn/xxgk/zcfb/tz/202406/P020240624568949062260.pdf',
    summary: '用更宏观的资料理解候鸟迁飞通道，而不只看单个湖泊。',
    audience: '家长查阅'
  },
  {
    id: 'csf-insects',
    topic: 'insects',
    kind: 'article',
    title: '《自然有发现》：身边的昆虫',
    source: '中国林学会',
    url: 'https://www.csf.org.cn/zhListDetail.html?contentId=59950&id=129',
    summary: '介绍昆虫的身体特征、常见类群和观察入口，适合补充草原小生命。',
    audience: '孩子可读'
  },
  {
    id: 'kepuchina-swallow',
    topic: 'birds',
    kind: 'article',
    title: '天下燕子一般黑？衔泥盖房小而美！',
    source: '科普中国',
    url: 'https://piyao.kepuchina.cn/h5/rumordetail?id=nweR',
    summary: '介绍家燕体形、剪刀尾、泥巢和在昆虫聚集处飞行捕食的习性。',
    audience: '孩子可读'
  },
  {
    id: 'white-birch',
    topic: 'species',
    kind: 'article',
    title: '白桦',
    source: 'iPlant 植物智',
    url: 'https://www.iplant.cn/info/Betula%20platyphylla?t=z',
    summary: '用专业植物物种信息核对白桦的中文名、学名、分类和图像资料。',
    audience: '家长讲解'
  },
  {
    id: 'reindeer-ewenki',
    topic: 'culture',
    kind: 'article',
    title: '鄂温克族历史沿革',
    source: '国家民委',
    url: 'https://www.neac.gov.cn/seac/ztzl/ewkz/lsyg.shtml',
    summary: '了解鄂温克族与森林生活、桦树皮、驯鹿和“使鹿部”等历史线索。',
    audience: '家长讲解'
  },
  {
    id: 'mongolian-yurt',
    topic: 'culture',
    kind: 'article',
    title: '蒙古族风俗文化',
    source: '国家民委',
    url: 'https://www.neac.gov.cn/seac/ztzl/mgz/fswh.shtml',
    summary: '了解蒙古包、奶茶、奶食、牛粪燃料和游牧生活之间的关系。',
    audience: '亲子共读'
  },
  {
    id: 'enhe-russian',
    topic: 'culture',
    kind: 'article',
    title: '俄罗斯族概况',
    source: '国家民委',
    url: 'https://www.neac.gov.cn/seac/ztzl/elsz/gk.shtml',
    summary: '了解呼伦贝尔市恩和俄罗斯族民族乡和中国俄罗斯族分布背景。',
    audience: '家长讲解'
  },
  {
    id: 'manzhouli-city',
    topic: 'culture',
    kind: 'article',
    title: '满洲里市',
    source: '百科资料',
    url: 'https://zh.wikipedia.org/wiki/%E6%BB%A1%E6%B4%B2%E9%87%8C%E5%B8%82',
    summary: '了解满洲里作为边境城市、铁路节点和口岸城市的背景。',
    audience: '家长讲解'
  },
  {
    id: 'ergun-city',
    topic: 'culture',
    kind: 'article',
    title: '额尔古纳市',
    source: '百科资料',
    url: 'https://zh.wikipedia.org/wiki/%E9%A2%9D%E5%B0%94%E5%8F%A4%E7%BA%B3%E5%B8%82',
    summary: '了解额尔古纳河右岸、边境位置，以及黑山头、室韦等口岸线索。',
    audience: '家长讲解'
  },
  {
    id: 'hulun-lake',
    topic: 'wetland',
    kind: 'article',
    title: '呼伦湖',
    source: '百科资料',
    url: 'https://zh.wikipedia.org/wiki/%E5%91%BC%E4%BC%A6%E6%B9%96',
    summary: '认识呼伦湖作为草原湖泊和候鸟停歇地的地理位置。',
    audience: '亲子共读'
  },
  {
    id: 'chenbarag-lakes',
    topic: 'wetland',
    kind: 'article',
    title: '陈巴尔虎旗自然与旅游概况',
    source: '陈巴尔虎旗政府网',
    url: 'http://www.cbrhq.gov.cn/OpennessContent/show/504261.html',
    summary: '了解陈巴尔虎旗草原、河流和以呼和诺尔湖为代表的湖泊资源。',
    audience: '家长查阅'
  },
  {
    id: 'species-database',
    topic: 'species',
    kind: 'database',
    title: '中国生物物种名录',
    source: '中国科学院动物研究所',
    url: 'https://ioz.cas.cn/gb2018/cg/sjk/',
    summary: '专业物种名录入口，后续可用来核对实地照片里的动物、鸟类和昆虫。',
    audience: '家长查阅'
  },
  {
    id: 'obao-ich',
    topic: 'culture',
    kind: 'article',
    title: '祭敖包',
    source: '中国非物质文化遗产网',
    url: 'https://www.ihchina.cn/Article/Index/detail?id=14997',
    summary: '解释敖包的含义、位置、礼俗和草原民族崇尚自然的观念。',
    audience: '亲子共读'
  }
];

export const sourceNotes = [
  '河曲地貌：参考中国国家地理、USGS、National Geographic Education 和 Britannica 对蜿蜒型河流、牛轭湖、冲刷与堆积过程的说明。',
  '草原生态：参考中国科学院植物研究所关于草原生产者、消费者、分解者和物质循环的科普文章。',
  '湿地功能：参考国家林业和草原局、中国科学院东北地理与农业生态研究所关于湿地涵养水源、净化水质、调蓄洪水和维护生物多样性的说明。',
  '鸟类迁飞：参考国家林业和草原局、国家发展改革委关于东亚-澳大利西亚候鸟迁飞路线和呼伦湖湿地的资料。',
  '敖包文化：参考中国非物质文化遗产网“祭敖包”条目。',
  'Day 1 城市历史：参考呼伦贝尔博物院、呼伦贝尔历史博物馆官网和内蒙古党史方志网关于呼伦贝尔城、正阳街一带建城与名称由来的资料。',
  '民族文化：参考国家民委蒙古族、鄂温克族、俄罗斯族专题资料；具体体验项目按现场讲解和当地资料再核验。',
  '植物资料：白桦等植物名称和图像资料优先参考 iPlant 植物智、中国生物物种名录等专业数据库。',
  '动物、昆虫、鸟类图鉴为儿童科普入口，图片使用本地化的公开授权素材并在卡片中标注来源；具体旅途中能否看见某物种需以后续实地照片和当地资料核验。'
];

export const previewChapters = {
  day1: {
    title: '草原旅行的起点',
    question: '为什么我们不一下飞机就冲进草原？',
    text: '第一天适合从城市进入草原节奏：海拉尔、历史博物馆、古城街区、天气和地图感，会帮助孩子准备好第二天的观察。',
    topics: ['海拉尔在哪里', '历史博物馆', '呼伦贝尔古城', '草原天气']
  },
  day3: {
    title: '白色树皮、马背和俄罗斯族村庄',
    question: '为什么一棵树能变成器物、故事和文化？',
    text: 'Day 3 会从额尔古纳白桦林出发，连接白桦树皮、手作材料、骑马体验和恩和木刻楞生活。',
    topics: ['白桦树皮', '骑马不是游乐项目', '非遗手作', '木刻楞']
  },
  day4: {
    title: '走进大兴安岭，寻找驯鹿的森林',
    question: '为什么驯鹿属于森林，而不是普通动物园？',
    text: 'Day 4 会进入森林线：驯鹿、苔藓地衣、落叶松、森林小火车和鄂温克生活都在这里相遇。',
    topics: ['驯鹿适应', '大兴安岭', '苔藓和地衣', '森林小火车']
  },
  day5: {
    title: '一条河的边境，一座坡的速度，一个蒙古包的家',
    question: '房子、食物和燃料为什么会被地理决定？',
    text: 'Day 5 会把边境河、坡度、云朵牧场、蒙古包、奶茶和羊毛毡放在一条生活逻辑里。',
    topics: ['界河', '滑草与坡度', '蒙古包', '奶茶和羊毛毡']
  },
  day6: {
    title: '草原电影感与边境童话城',
    question: '为什么满洲里看起来不像普通中国城市？',
    text: 'Day 6 会从草原走向边境城市，理解套娃、铁路、口岸、贸易和俄式建筑符号。',
    topics: ['呼籁秘境', '套娃文化', '边境口岸', '俄式建筑']
  },
  day7: {
    title: '湖边告别，草原还在继续',
    question: '为什么湖泊会改变草原？',
    text: 'Day 7 会把马之舞、呼和诺尔湖和旅行回忆收束成一次告别，也留下继续观察自然的方式。',
    topics: ['马之舞', '呼和诺尔湖', '湖泊草原', '旅行记忆']
  }
} as const;
